import type { QueryClient } from '@tanstack/react-query'
import { sessionKeys } from './keys.ts'
import type { Me } from './useMe.ts'

/**
 * Who a cached answer belongs to: the signed-in user *in* the active org.
 * Either changing means every other cached answer was someone else's.
 */
export function sessionIdentity(me: Me | null | undefined): string | null {
  return me ? `${me.user.id}:${me.activeOrganizationId ?? ''}` : null
}

/**
 * Drops every cached query when the session the server just confirmed is not
 * the one the cache was filled for.
 *
 * A cache persisted across reloads (the web kiosk's offline boot) is restored
 * before anybody is asked who is signed in. If the session changed outside
 * this page — another tab, an expiry and a different sign-in — the restored
 * entries are another person's directory, contacts and permissions, served
 * until they go stale. That is a leak with a 30-second fuse, so the first
 * confirmed identity of a boot is compared against the restored one and a
 * mismatch empties the cache before anything renders from it.
 *
 * Returns true when it dropped anything.
 */
export function dropForeignCache(
  queryClient: QueryClient,
  restored: Me | null | undefined,
  confirmed: Me | null,
): boolean {
  if (restored === undefined || sessionIdentity(restored) === sessionIdentity(confirmed)) {
    return false
  }
  const meKey = JSON.stringify(sessionKeys.me())
  queryClient.removeQueries({ predicate: (query) => JSON.stringify(query.queryKey) !== meKey })
  return true
}
